import { type PendingSkillInstruction } from '../types/pendingSkillInstruction';
import { extractSlashCommandsFromMessage } from './slashCommandInMessage';

/** Collapses accidental duplicate paragraphs (e.g. repeated blocks in stored skill copy). */
export function dedupeInstructionParagraphs(text: string): string {
  const paragraphs = text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const p of paragraphs) {
    if (!seen.has(p)) {
      seen.add(p);
      unique.push(p);
    }
  }

  return unique.join('\n\n');
}

/**
 * Returns the skillInstructions context value for the outgoing user message.
 * Only includes instructions for slash commands that actually appear in the message body,
 * so stale skills from earlier edits or thread switches cannot attach to the wrong send.
 */
export function selectSkillInstructionsForMessage(
  pending: PendingSkillInstruction[],
  message: string,
): string | undefined {
  if (pending.length === 0) {
    return undefined;
  }

  const commandsInMessage = extractSlashCommandsFromMessage(message);

  if (commandsInMessage.size === 0) {
    return undefined;
  }

  const relevant = pending.filter((entry) => commandsInMessage.has(entry.slashCommand));

  if (relevant.length === 0) {
    return undefined;
  }

  const combined = relevant
    .map((entry) => dedupeInstructionParagraphs(entry.instructions.trim()))
    .filter(Boolean)
    .join('\n\n');

  const deduped = dedupeInstructionParagraphs(combined);

  return deduped.length > 0 ? deduped : undefined;
}
